import * as Yup from 'yup';
import { appConfig, brandConstants } from 'brand-config';

import { MISSING_PRIMARY_HOMEOWNER_FIELD } from 'core/components/validation-errors-modal/validation-errors-modal.constants';

// CFM Auto must accompany a primary homeowner policy with the carrier (#35914). The Companion Policy
// section asks "Primary Homeowner policy with <brand>?" (auto.primaryResidentHomeowner, Yes/No). These
// rules are shared by the offer and policy-details auto validation schemas so the field name, message
// and logic live in one place — mirrors the farm insures-nothing rule, which is likewise shared.
// Brand-gated on collectAutoCompanionPolicy (CFM only today); read at validate-time so the guard
// tracks the active brand.
const collectsCompanionPolicy = () => appConfig?.featureFlags?.collectAutoCompanionPolicy ?? false;

// Message rendered in the validation-errors modal's Blocking issues section, keyed to the shared
// MISSING_PRIMARY_HOMEOWNER_FIELD so the modal renders it there (not as a fixable card). Built from the
// brand short name so it matches the "Primary Homeowner policy with <brand>?" field label in
// companion-policy.jsx rather than hardcoding "CFM".
const missingPrimaryHomeownerMessage = () => `Must have Primary Homeowner Policy with ${brandConstants.SHORT_NAME}`;

// True when the primary-homeowner answer is untouched from the stored value. `grandfather` uses this
// so servicing an existing or Guidewire-imported CFM auto isn't blocked for an unrelated endorsement:
// the importer historically set primaryResidentHomeowner from multiPolicyDiscount and never set
// Companion Type/Number, so enforcing those retroactively would lock every edit behind a backfill.
// The same "grandfather migrated data" lesson as the auto adjustment factor (#35431; devlo on #35926).
// The hard-stop wrapper passes grandfather:false for new-business offers so a blank/No answer is still
// forced to Yes. The shape-field requirements pass grandfather:true (offer and policy-details alike)
// but additionally only skip when the STORED field was blank (storedFieldBlank), so a migrated Yes
// with no Type/Number is grandfathered while a saved Yes whose Type/Number is later cleared is not.
// (#35992)
const primaryHomeownerAnswerUnchanged = (ctx, current) =>
  current === ctx.options?.context?.initialValues?.auto?.primaryResidentHomeowner;

// True when the given companion field had no stored value (migrated/missing), so grandfathering fills
// a gap rather than permitting deletion of previously-saved data. initialValues.auto is reseeded from
// the saved offer via enableReinitialize, so this reflects what was actually persisted. #35992
const storedFieldBlank = (ctx, field) => !ctx.options?.context?.initialValues?.auto?.[field];

// Companion Policy Type/Number identify the homeowner policy, so they're required once the answer is
// Yes. Spread into the auto object shape. `grandfather` skips the requirement for a pre-existing or
// imported Yes (an unchanged answer), so only a newly selected Yes must supply the details.
export const companionPolicyShapeFields = ({ grandfather }) => ({
  companionPolicyType: Yup.string()
    .nullable()
    .test('companion-type-required-when-homeowner', 'Companion policy type is required', function (value) {
      if (!collectsCompanionPolicy() || this.parent?.primaryResidentHomeowner !== true) {
        return true;
      }
      if (
        grandfather &&
        primaryHomeownerAnswerUnchanged(this, this.parent?.primaryResidentHomeowner) &&
        storedFieldBlank(this, 'companionPolicyType')
      ) {
        return true;
      }
      return !!value;
    }),
  companionPolicyNumber: Yup.string()
    .nullable()
    .test('companion-number-required-when-homeowner', 'Companion policy number is required', function (value) {
      if (!collectsCompanionPolicy() || this.parent?.primaryResidentHomeowner !== true) {
        return true;
      }
      if (
        grandfather &&
        primaryHomeownerAnswerUnchanged(this, this.parent?.primaryResidentHomeowner) &&
        storedFieldBlank(this, 'companionPolicyNumber')
      ) {
        return true;
      }
      return !!value;
    })
});

// Hard stop: unless "Primary Homeowner policy with CFM?" is answered Yes, raise a blocking error on
// the dedicated synthetic field so the validation-errors modal shows it in the red Blocking issues
// lane. Applied as a wrapper (not chained onto the shape) so the big auto object literal keeps its
// formatting — mirrors the farm insures-nothing rule. `grandfather` lets an untouched stored answer
// through so in-force / imported policies stay serviceable.
export const withPrimaryHomeownerPolicyCheck =
  ({ grandfather }) =>
  (schema) =>
    schema.test('primary-homeowner-policy-with-cfm', function (value) {
      const current = value?.auto?.primaryResidentHomeowner;
      if (!collectsCompanionPolicy() || current === true) {
        return true;
      }
      if (grandfather && primaryHomeownerAnswerUnchanged(this, current)) {
        return true;
      }
      return this.createError({ path: MISSING_PRIMARY_HOMEOWNER_FIELD, message: missingPrimaryHomeownerMessage() });
    });
